import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

function FeWheelPanel({
  feWheel,
  templates,
  status,
  onAssign,
  onUnassign,
  saving,
}) {
  const [templateId, setTemplateId] = useState('');

  useEffect(() => {
    setTemplateId(feWheel?.template_id ? String(feWheel.template_id) : '');
  }, [feWheel?.template_id]);

  const assigned = !!feWheel?.assigned || !!feWheel?.template_id;
  const snapshot = feWheel?.wheel_assignment_snapshot || feWheel?.snapshot || null;
  const sectors = snapshot?.sectors || feWheel?.sectors || [];
  const isCompleted = status === 'completed';

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Assign a reusable wheel template for this IST date. Assignment freezes a{' '}
        <code>wheel_assignment_snapshot</code>. Manage templates in the library — independent from
        the global default wheel.
      </Alert>

      <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mb={2}>
        <TextField
          select
          size="small"
          label="Template"
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
          sx={{ minWidth: 280 }}
          disabled={isCompleted || saving}
        >
          <MenuItem value="">— none —</MenuItem>
          {(templates || []).map((t) => (
            <MenuItem key={t.id} value={String(t.id)}>
              {t.title || `Template ${t.id}`}
              {t.is_enabled === false ? ' (disabled)' : ''}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="contained"
          disabled={!templateId || saving || isCompleted}
          onClick={() => onAssign(Number(templateId))}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Assign template
        </Button>
        {assigned && (
          <Button
            color="warning"
            disabled={saving || isCompleted}
            onClick={onUnassign}
          >
            Unassign
          </Button>
        )}
        <Button
          component={Link}
          to="/admin/daily-tasks/wheel-templates"
          endIcon={<OpenInNewIcon fontSize="small" />}
          size="small"
        >
          Template library
        </Button>
      </Box>

      {isCompleted && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Completed past dates cannot change wheel assignment.
        </Alert>
      )}

      {assigned ? (
        <>
          <Typography variant="subtitle2" gutterBottom>
            {feWheel?.title || snapshot?.title || 'Assigned wheel'}{' '}
            <Chip size="small" label={`template #${feWheel?.template_id}`} sx={{ ml: 1 }} />
            {(feWheel?.sector_count || sectors.length) > 0 && (
              <Chip
                size="small"
                label={`${feWheel?.sector_count || sectors.length} sectors`}
                sx={{ ml: 0.5 }}
              />
            )}
          </Typography>
          {sectors.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>FE</TableCell>
                  <TableCell>Weight</TableCell>
                  <TableCell>%</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sectors.map((s, i) => (
                  <TableRow key={s.id || i}>
                    <TableCell>{s.sort_order ?? i + 1}</TableCell>
                    <TableCell>{s.sector_type || '—'}</TableCell>
                    <TableCell>{s.label || '—'}</TableCell>
                    <TableCell>{s.fe_amount ?? '—'}</TableCell>
                    <TableCell>{s.weight ?? '—'}</TableCell>
                    <TableCell>{s.probability_percent != null ? `${s.probability_percent}%` : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      ) : (
        <Typography color="text.secondary">No wheel template assigned for this date.</Typography>
      )}
    </Box>
  );
}

export default FeWheelPanel;
